import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Search, FileText, ArrowLeft, Home } from 'lucide-react';
import { useGlobalSearch, SearchResult } from '@/hooks/useGlobalSearch';

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  const { results, isSearching } = useGlobalSearch(query);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(query)}`, { replace: true });
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
  };

  const highlightMatch = (text: string, searchQuery: string) => {
    if (!searchQuery.trim()) return text;
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-primary/30 text-foreground rounded px-0.5">{part}</mark>
      ) : part
    );
  };

  // MEDIUM-5: converteer [[HL]]/[[/HL]] API-markers naar <mark>
  const renderSnippet = (snippet: string) => {
    const parts = snippet.split(/(\[\[HL\]\]|\[\[\/HL\]\])/);
    let inMark = false;
    return parts.map((part, i) => {
      if (part === '[[HL]]') { inMark = true; return null; }
      if (part === '[[/HL]]') { inMark = false; return null; }
      return inMark
        ? <mark key={i} className="bg-primary/30 text-foreground rounded px-0.5">{part}</mark>
        : part;
    });
  };

  // Group results by tenant
  const groupedResults = results.reduce((acc, result) => {
    const key = result.tenant;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  return (
    <div className="min-h-screen bg-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <Link
              to="/dashboard"
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Go to dashboard"
            >
              <Home className="w-5 h-5 text-muted-foreground" />
            </Link>
            <h1 className="text-xl font-semibold text-foreground">Search Results</h1>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search across all documentation..."
              className="w-full pl-12 pr-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </form>
        </div>
      </header>

      {/* Results */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {query.trim() === '' ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-lg font-medium text-foreground mb-2">Enter a search term</h2>
            <p className="text-muted-foreground">Start typing to search across all documentation</p>
          </div>
        ) : isSearching ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Searching...</p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-lg font-medium text-foreground mb-2">No results found</h2>
            <p className="text-muted-foreground">
              No documentation matches "{query}". Try different keywords.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            <p className="text-sm text-muted-foreground">
              Found <span className="font-medium text-foreground">{results.length}</span> result{results.length !== 1 ? 's' : ''} for "{query}"
            </p>

            {Object.entries(groupedResults).map(([tenant, tenantResults]) => (
              <div key={tenant}>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  {tenant}
                </h2>
                
                <div className="space-y-3">
                  {tenantResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      className="w-full p-5 bg-card border border-border rounded-xl text-left hover:border-primary/50 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {highlightMatch(result.title, query)}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                            {renderSnippet(result.snippet)}
                          </p>
                          <div className="flex items-center gap-3 mt-3">
                            <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-md">
                              {result.docType}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {result.path}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
